import type { ReactElement } from 'react';
import React, { useMemo } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { ChoiceGroup } from '../ChoiceGroup';
import { useProductChoiceGroup } from './useProductChoiceGroup';
import { useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { useProductToUIMapping } from '@apps/card/components/ProductUIConfig/useProductToUIMapping';
import { useMinorUnitsToPriceString } from '@apps/card/hooks/useMinorUnitsToPriceString';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import type { Product } from '@apps/card/components/ProductUIConfig/product.types';
import { useTranslation } from '@shared/core';
import { usePromotionList, isPromoValid, isCodeExpired } from '@apps/card/routes/CardCustomizer/usePromotionConfig';
import type { PromotionVendor } from '@apps/card/routes/CardCustomizer/usePromotionConfig';
import { usePromoConfigToStationeryDiscount } from '@shared/utils/print-price/utils/promoConfigToStationeryDiscount';
import { useCustomizationContext } from '@apps/card/routes/CardCustomizer/useCardCustomization';
import { useSalesConfig } from '@apps/card/routes/CardCustomizer/useSalesConfig';

const isProduct = (input: unknown): input is StationeryProduct & Product => !!input;

const DiscountedPriceText = ({ originalPrice, newPrice, saleColor }: { originalPrice: string; newPrice: string; saleColor: string }) => {
  const { tq } = useTranslation('stationery');
  return (
    <TextV2>
      <TextV2
        as="span"
        typographyVariant="body3"
      >{tq`From`}</TextV2>{' '}
      <TextV2
        as="span"
        textDecoration="line-through"
        color="mono10"
        typographyVariant="body3"
      >
        {originalPrice}
      </TextV2>{' '}
      <TextV2
        as="span"
        color={saleColor}
        fontWeight={600 as const}
        typographyVariant="body3"
      >
        {newPrice}
      </TextV2>
    </TextV2>
  );
};

const ActionItem = ({ selected, title, price }: { selected: boolean; title: string; price?: string | ReactElement }) => {
  return (
    <Flex
      justifyContent="space-between"
      alignItems="center"
      flex={1}
      paddingX={3}
    >
      <TextV2
        typographyVariant="body1"
        color="mono12"
        textTransform="capitalize"
        fontWeight={selected ? 600 : 400}
      >
        {title}
      </TextV2>
      {typeof price === 'string' ? (
        <TextV2
          typographyVariant="body3"
          color="mono12"
          fontWeight={selected ? 600 : 400}
        >
          {price}
        </TextV2>
      ) : (
        price
      )}
    </Flex>
  );
};

type ProductChoiceGroupProps = Readonly<{
  draftId: string;
}>;

type ProductChoiceGroupContentProps = Readonly<{
  isSwitching: boolean;
  selectProduct: (productId: string) => void;
}>;

const ProductChoiceGroupContent = ({ isSwitching, selectProduct }: ProductChoiceGroupContentProps) => {
  const { products: productInfo, activeProduct, priceCalculator } = useStationeryProductPricingContext();
  const { getProductWithUIMapping } = useProductToUIMapping();
  const minorUnitsToPriceString = useMinorUnitsToPriceString();
  const { tq } = useTranslation('stationery');
  const { promoConfigToStationeryDiscount } = usePromoConfigToStationeryDiscount();
  const [templateCategory] = useCustomizationContext(draft => draft.stationeryTemplateCategory);
  const [savedPromoCode] = useCustomizationContext(draft => draft.config.customizations.promoCode);
  const { promotions } = usePromotionList(templateCategory);
  const { sales, isEnabled: isSaleEnabled, currentSale } = useSalesConfig(templateCategory);
  const saleColor = (() => {
    const color = isSaleEnabled && currentSale ? currentSale.color : undefined;
    return typeof color === 'string' && color.length > 0 ? color : '#6A36FF';
  })();
  const currentPromotion = useMemo(() => {
    if (typeof savedPromoCode !== 'string') return undefined;
    const product = { occasion: 'wedding', category: templateCategory };
    return promotions.find(p => isPromoValid(p, product) && p.code.toLowerCase() === savedPromoCode.toLowerCase());
  }, [savedPromoCode, templateCategory, promotions]);

  const products = productInfo.map(p => getProductWithUIMapping(p)).filter(isProduct);

  return (
    <Box
      opacity={isSwitching ? 0.6 : 1}
      pointerEvents={isSwitching ? 'none' : 'auto'}
      transition="opacity 0.15s ease"
    >
      <ChoiceGroup
        title={tq`Product`}
        getName={() => (products.length === 1 ? activeProduct.translations.formatName : '')}
        value={activeProduct.id}
        minHeight={55}
        choices={products.map(p => p.id)}
        setValue={selectProduct}
        columns={1}
      >
        {products.map(product => {
          const matchingSale = sales?.find(
            sale => isPromoValid(sale, { occasion: 'wedding', category: templateCategory, vendor: product.vendor as string }) && !isCodeExpired(sale),
          );
          const discount = promoConfigToStationeryDiscount(matchingSale ?? currentPromotion, templateCategory, product.vendor as PromotionVendor);
          const priceData = priceCalculator.getProductBasePrice(product.id, product.defaultQuantity, discount, true);
          const unitPriceString = minorUnitsToPriceString(priceData.unitPriceInMinorUnits, { canShowFreeLabel: true });
          const preDiscountUnitPriceString = priceData.discounts && minorUnitsToPriceString(priceData.discounts.preDiscountedUnitPriceInMinorUnits, { canShowFreeLabel: true });
          const isDiscountDifferent = preDiscountUnitPriceString !== unitPriceString;
          const canDisplayDiscount = discount && preDiscountUnitPriceString && isDiscountDifferent;

          const priceDisplay = canDisplayDiscount ? (
            <DiscountedPriceText
              originalPrice={preDiscountUnitPriceString}
              newPrice={unitPriceString}
              saleColor={saleColor}
            />
          ) : priceData.unitPriceInMinorUnits === 0 ? (
            unitPriceString
          ) : (
            tq`From ${unitPriceString}`
          );

          return (
            <ActionItem
              key={product.id}
              selected={product.id === activeProduct.id}
              title={product.translations.displayName}
              price={priceDisplay}
            />
          );
        })}
      </ChoiceGroup>
    </Box>
  );
};
// The pdp screen choicegroup is not used here because it involves a lot of complicated issues, such as cross app imports and exports,
//contexts and other dependencies and also the callback functions are not the same.
//It was simply not worth the effort to re-use the pdp screen choicegroup and not in the scope of this PR.
export const ProductChoiceGroup = ({ draftId }: ProductChoiceGroupProps) => {
  const { selectProduct, isSwitching, canSelectProduct } = useProductChoiceGroup({ draftId });
  if (!canSelectProduct) return null;
  return (
    <ProductChoiceGroupContent
      isSwitching={isSwitching}
      selectProduct={selectProduct}
    />
  );
};
